import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Zap, Loader2, Search, Users } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tela exclusiva do super_admin: liberar ou revogar o modo automático
 * 24/7 para um afiliado específico - regra de negócio confirmada: dois
 * modos existem (manual, padrão de todos; automático, upgrade que só o
 * dono do sistema concede, um afiliado por vez).
 */
const AutomacaoAfiliados = () => {
  const [emailBusca, setEmailBusca] = useState("");
  const [afiliado, setAfiliado] = useState(null);
  const [statusAutomacao, setStatusAutomacao] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const buscar = async () => {
    if (!emailBusca) {
      toast.error("Digite o e-mail do afiliado.");
      return;
    }
    setBuscando(true);
    setAfiliado(null);
    try {
      const resp = await authService.fetchAuth(
        `${BACKEND_URL}/api/perfil/buscar-por-email?email=${encodeURIComponent(emailBusca)}`
      );
      if (!resp.ok) throw new Error("Afiliado não encontrado.");
      const dados = await resp.json();
      setAfiliado(dados);

      const respStatus = await authService.fetchAuth(`${BACKEND_URL}/api/perfil/automacao-24-7/${dados.id}`);
      if (respStatus.ok) {
        const statusData = await respStatus.json();
        setStatusAutomacao(statusData.automacao_24_7_liberada);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBuscando(false);
    }
  };

  const alternarAutomacao = async (liberar) => {
    setSalvando(true);
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/perfil/automacao-24-7/${afiliado.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ liberado: liberar }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao atualizar");
      setStatusAutomacao(liberar);
      toast.success(liberar ? "Modo automático 24/7 liberado para este afiliado." : "Modo automático 24/7 revogado.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Zap className="w-7 h-7 text-orange-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Modo Automático 24/7</h1>
          <p className="text-sm text-gray-400">Libere ou revogue, afiliado por afiliado - só você decide</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="E-mail do afiliado"
            value={emailBusca}
            onChange={(e) => setEmailBusca(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && buscar()}
            className="pl-9"
          />
        </div>
        <Button onClick={buscar} disabled={buscando} className="bg-orange-600 hover:bg-orange-500">
          {buscando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
        </Button>
      </div>

      {afiliado && (
        <Card className="p-5 bg-gray-800/50 border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-8 h-8 text-gray-500" />
            <div>
              <p className="text-white font-medium">{afiliado.nome}</p>
              <p className="text-xs text-gray-400">{afiliado.email}</p>
              {afiliado.plano && (
                <span className="text-[10px] bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full mt-1 inline-block">
                  Plano: {afiliado.plano}
                </span>
              )}
            </div>
          </div>

          <div className={`p-3 rounded-lg mb-4 ${statusAutomacao ? "bg-emerald-950/30 border border-emerald-800" : "bg-gray-900 border border-gray-700"}`}>
            <p className={`text-sm font-medium ${statusAutomacao ? "text-emerald-400" : "text-gray-400"}`}>
              {statusAutomacao ? "✓ Modo automático 24/7 liberado" : "Modo manual (padrão)"}
            </p>
          </div>

          {statusAutomacao ? (
            <Button
              variant="outline"
              onClick={() => alternarAutomacao(false)}
              disabled={salvando}
              className="w-full border-red-800 text-red-400 hover:bg-red-950/30"
            >
              {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Revogar Automático 24/7
            </Button>
          ) : (
            <Button
              onClick={() => alternarAutomacao(true)}
              disabled={salvando}
              className="w-full bg-orange-600 hover:bg-orange-500"
            >
              {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Liberar Automático 24/7
            </Button>
          )}
        </Card>
      )}
    </div>
  );
};

export default AutomacaoAfiliados;
